import { ComponentProps, memo, useCallback, useEffect, useRef } from "react";
import MonacoEditor, { loader } from "@monaco-editor/react";
import * as monacoRuntime from "monaco-editor";
import EditorWorker from "monaco-editor/esm/vs/editor/editor.worker?worker";
import TsWorker from "monaco-editor/esm/vs/language/typescript/ts.worker?worker";
import { initVimMode, InitVimModeResult } from "monaco-vim";
import { useStore } from "statelift";
import { documentsStore } from "../../stores/documents";
import { useUserOptions } from "../../stores/user-options";

self.MonacoEnvironment = {
  getWorker: (_workerId, label) =>
    label === "typescript" || label === "javascript" ? new TsWorker() : new EditorWorker(),
};
loader.config({ monaco: monacoRuntime });

type MonacoMountHandler = ComponentProps<typeof MonacoEditor>["onMount"];
type IStandaloneCodeEditor = Parameters<Exclude<MonacoMountHandler, undefined>>[0];

const EDITOR_THEME_NAMES = {
  light: "vs",
  dark: "vs-dark",
} as const;

const editorOptions: ComponentProps<typeof MonacoEditor>["options"] = {
  minimap: { enabled: false },
  renderLineHighlight: "none",
  fontSize: 15,
  scrollBeyondLastLine: false,
  scrollbar: {
    vertical: "auto",
    horizontal: "auto",
  },
};

export const Editor = memo(() => {
  const options = useUserOptions();
  const currentDocumentSource = useStore(documentsStore, (state) => state.currentDocument.source);

  const editorRef = useRef<IStandaloneCodeEditor | null>(null);
  const vimModeRef = useRef<InitVimModeResult | null>(null);
  const vimStatusLineRef = useRef<HTMLDivElement>(null);

  const isVimMode = options.editor.editingMode === "vim";

  const handleSourceChange = useCallback((value: string | undefined) => {
    documentsStore.state.setCurrentDocumentSource(value ?? "");
  }, []);

  const handleMount: MonacoMountHandler = (mountedEditor, mountedMonaco) => {
    editorRef.current = mountedEditor;

    const compilerOptions = mountedMonaco.languages.typescript.typescriptDefaults.getCompilerOptions();
    compilerOptions.target = mountedMonaco.languages.typescript.ScriptTarget.Latest;
    compilerOptions.lib = ["esnext"];
    mountedMonaco.languages.typescript.typescriptDefaults.setCompilerOptions(compilerOptions);
    mountedEditor.updateOptions({ cursorStyle: isVimMode ? "block" : "line" });
    mountedEditor.getModel()?.updateOptions({ tabSize: 2, indentSize: 2 });
    mountedEditor.focus();

    if (isVimMode) {
      if (!vimStatusLineRef.current) throw new Error("vimStatusLineRef.current is null");
      vimModeRef.current = initVimMode(mountedEditor, vimStatusLineRef.current);
      vimModeRef.current.on("vim-mode-change", ({ mode }) => {
        if (!editorRef.current) return;
        mountedEditor.updateOptions({ cursorStyle: mode === "insert" ? "line" : "block" });
      });
    }
  };

  useEffect(() => {
    if (isVimMode) {
      if (vimModeRef.current) return;
      if (!editorRef.current) return;
      if (!vimStatusLineRef.current) return;
      vimModeRef.current = initVimMode(editorRef.current, vimStatusLineRef.current);
      editorRef.current.updateOptions({ cursorStyle: "block" });
    } else {
      vimModeRef.current?.dispose();
      vimModeRef.current = null;
      editorRef.current?.updateOptions({ cursorStyle: "line" });
    }
  }, [isVimMode]);

  return (
    <div className="flex flex-col h-full">
      <MonacoEditor
        defaultLanguage="typescript"
        wrapperProps={{ className: "min-h-0 flex-1" }}
        options={editorOptions}
        theme={EDITOR_THEME_NAMES[options.renderer.theme]}
        value={currentDocumentSource}
        onChange={handleSourceChange}
        onMount={handleMount}
      />
      {isVimMode && (
        <div
          ref={vimStatusLineRef}
          className="shrink-0 border-t border-border bg-inset px-2 py-1 font-mono text-ui text-text vim-status"
        />
      )}
    </div>
  );
});
